import awsmobile from '@/aws-exports';
import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { getLinkDownloadFileFromS3 } from '@/component/common/FileUploaded';
import SpecialCharactersEmoji from '@/component/formManagement/fieldUI/SpecialCharactersEmoji';
import { uploadFileToS3InTMPFolder, uploadFileToS3InPath } from '@/common/utils/UploadFileUtil';
import { getRichTextFieldContent } from '@/component/ticketList/handler/TicketHandler';
import { I18n } from 'aws-amplify';
import { cloneDeep } from 'lodash';
import localStorageUtil from '@/common/utils/localStorageUtil';

function getImageSrc(htmlString) {
    let doc;
    try {
        doc = new DOMParser().parseFromString(htmlString, 'text/html');
    } catch (e) {
        return [];
    }

    const img = doc.querySelectorAll('img');
    const imgSrcList = [];
    if (img) {
        doc.querySelectorAll('img').forEach(imgElement => {
            imgSrcList.push(imgElement.getAttribute('src'));
        });
    }
    return imgSrcList;
}

const replaceOldLinkImg = defaultValue => {
    try {
        if (defaultValue)
            defaultValue = defaultValue.replace(/\\\n/g, ' <br /> ').replace(/\\n/g, ' <br /> ');
    } catch (error) {}
    // console.log('defaultValue 2', defaultValue)
    let stringCopy = (' ' + defaultValue).slice(1);

    let imgSrcList = getImageSrc(defaultValue);
    imgSrcList = imgSrcList.filter(src => src);
    if (Utils.isEmptyArray(imgSrcList)) {
        return stringCopy;
    }

    for (let i = 0; i < imgSrcList.length; i++) {
        let indexOfImage = imgSrcList[i].indexOf(
            `https://${awsmobile.aws_user_files_s3_bucket}.s3`
        );
        if (indexOfImage !== -1) {
            const startStr = '/public/';
            const endStr = '?';

            const startIndex = imgSrcList[i].indexOf(startStr) + startStr.length;
            const endIndex = imgSrcList[i].indexOf(endStr, startIndex);

            const resultKey = imgSrcList[i].substring(startIndex, endIndex);

            let fileUpload = {
                fileUrl: resultKey,
                type: Constant.S3_FILE_TYPE.TICKET_FILE
            };
            let link = getLinkDownloadFileFromS3(fileUpload, {}, null, null, false, true);
            let linkNeedReplace = `\\"${link}\\"`;
            stringCopy = stringCopy.replace(imgSrcList[i], linkNeedReplace);
        }
    }
    return stringCopy;
};

export const RichTextSettingField = props => {
    const ckEditorId = Utils.generateRandomStr();
    const timeStampDefault = new Date().getTime();
    const [selftCKEditor5, setSelftCKEditor5] = useState(null);
    
    useEffect(() => {
        const elem = document.querySelector(`.custom-class${timeStampDefault}`);
        if (elem) {
            let visible = window?.getComputedStyle(elem)?.getPropertyValue('visibility');
            let elemUploadFile = elem.querySelector('.rdw-image-modal-upload-option');
            if(elemUploadFile) {
                elemUploadFile.addEventListener('click', () =>{
                    elem.style.display = 'flex';
                })
            }
            if (visible === 'visible') {
                elem.style.display = 'flex';
            } else {
                elem.style.display = 'none';
            }
        }
    });
    /**
     * Custom image upload to S3 storage.
     *
     * Ref: https://ckeditor.com/docs/ckeditor5/latest/framework/deep-dive/upload-adapter.html
     */
    function uploadImageAdapterPlugin(editor) {
        editor.plugins.get('FileRepository').createUploadAdapter = loader => {
            return handleUploadImageAdapter(loader);
        };
    }
    const handleUploadImageAdapter = loader => {
        return {
            upload: async () => {
                return loader.file.then(async file => {
                    const timeStamp = new Date().getTime();
                    const affiliationOrganization = localStorageUtil.getAffiliationOrganization();
                    const pathS3Key = `assets/${affiliationOrganization}/iframesetting/${timeStamp}`
                    const result = await uploadFileToS3InPath(file, pathS3Key);
                    let fileUpload = {
                        fileUrl: result.key,
                        type: Constant.S3_FILE_TYPE.TICKET_FILE
                    };
                    let link = await getLinkDownloadFileFromS3(
                        fileUpload,
                        {},
                        null,
                        null,
                        false,
                        true
                    );
                    return {
                        default: `${link}`
                    };
                });
            }
        };
    };

    const editorConfiguration = {
        language: 'en',
        font_defaultLabel: 'Arial, Helvetica, sans-serif',
        placeholder: '',
        extraPlugins: [SpecialCharactersEmoji, uploadImageAdapterPlugin],
        // https://ckeditor.com/docs/ckeditor5/latest/features/toolbar/toolbar.html#extended-toolbar-configuration-format
        toolbar: {
            items: [
                {
                    label: 'More basic styles',
                    icon: 'bold',
                    items: [
                        'bold',
                        'italic',
                        'strikethrough',
                        'underline',
                        'subscript',
                        'superscript',
                        'removeFormat'
                    ]
                },
                'heading',
                '|',
                'fontSize',
                'fontFamily',
                'fontColor',
                'fontBackgroundColor',
                '|',
                'bulletedList',
                'alignment',
                'todoList',
                'numberedList',
                '|',
                'insertTable',
                'specialCharacters',
                'link',
                'mediaEmbed',
                'uploadImage',
                'insertImage',
                'htmlEmbed',
                '|',
                'undo',
                'redo',
                'blockQuote',
                'codeBlock'
            ],
            shouldNotGroupWhenFull: true
        },
        mediaEmbed: {
            removeProviders: ['instagram', 'twitter', 'googleMaps', 'flickr', 'facebook']
        },
        image: {
            styles: ['alignCenter', 'alignLeft', 'alignRight'],
            resizeOptions: [
                {
                    name: 'resizeImage:original',
                    label: 'Original',
                    value: null
                },
                {
                    name: 'resizeImage:50',
                    label: '50%',
                    value: '50'
                },
                {
                    name: 'resizeImage:75',
                    label: '75%',
                    value: '75'
                }
            ],
            toolbar: [
                'imageTextAlternative',
                'toggleImageCaption',
                '|',
                'imageStyle:inline',
                'imageStyle:wrapText',
                'imageStyle:breakText',
                '|',
                'resizeImage'
            ]
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties',
                'tableCellProperties']
        },
        list: {
            properties: {
                styles: true,
                startIndex: true,
                reversed: true
            }
        },
        // https://ckeditor.com/docs/ckeditor5/latest/features/headings.html#configuration
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
            ]
        },
        // https://ckeditor.com/docs/ckeditor5/latest/features/font.html#configuring-the-font-family-feature
        fontFamily: {
            options: [
                // 'Arial',
                'Arial, Helvetica, sans-serif',
                'Georgia, serif',
                'Tahoma, Geneva, sans-serif',
                'Times New Roman, Times, serif',
                'Trebuchet MS, Helvetica, sans-serif',
                'Verdana, Geneva, sans-serif'
            ],
            supportAllValues: true
        },
        // https://ckeditor.com/docs/ckeditor5/latest/features/font.html#configuring-the-font-size-feature
        fontSize: {
            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
            supportAllValues: true
        },
        // Be careful with the setting below. It instructs CKEditor to accept ALL HTML markup.
        // https://ckeditor.com/docs/ckeditor5/latest/features/general-html-support.html#enabling-all-html-features
        htmlSupport: {
            allow: [
                {
                    name: /.*/,
                    attributes: true,
                    classes: true,
                    styles: true
                }
            ]
        },
        // Be careful with enabling previews
        // https://ckeditor.com/docs/ckeditor5/latest/features/html-embed.html#content-previews
        htmlEmbed: {
            showPreviews: true
        },
        // https://ckeditor.com/docs/ckeditor5/latest/features/link.html#custom-link-attributes-decorators
        link: {
            decorators: {
                openInNewTab: {
                    mode: 'manual',
                    label: 'Open in a new tab',
                    defaultValue: true,
                    attributes: {
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }
                }
            }
        },
        // The "superbuild" contains more premium features that require additional configuration, disable them below.
        // Do not turn them on unless you read the documentation and know how to configure them and setup the editor.
        removePlugins: [
            // These two are commercial, but you can try them out without registering to a trial.
            // 'ExportPdf',
            // 'ExportWord',
            'AIAssistant',
            'CKBox',
            'CKFinder',
            'EasyImage',
            // This sample uses the Base64UploadAdapter to handle image uploads as it requires no configuration.
            // https://ckeditor.com/docs/ckeditor5/latest/features/images/image-upload/base64-upload-adapter.html
            // Storing images as Base64 is usually a very bad idea.
            // Replace it on production website with other solutions:
            // https://ckeditor.com/docs/ckeditor5/latest/features/images/image-upload/image-upload.html
            'Base64UploadAdapter',
            'MultiLevelList',
            'RealTimeCollaborativeComments',
            'RealTimeCollaborativeTrackChanges',
            'RealTimeCollaborativeRevisionHistory',
            'PresenceList',
            'Comments',
            'TrackChanges',
            'TrackChangesData',
            'RevisionHistory',
            'Pagination',
            // Requires additional license key
            'WProofreader',
            // Careful, with the Mathtype plugin CKEditor will not load when loading this sample
            // from a local file system (file://) - load this site via HTTP server if you enable MathType.
            'MathType',
            // The following features are part of the Productivity Pack and require additional license.
            'SlashCommand',
            'Template',
            'DocumentOutline',
            'FormatPainter',
            'TableOfContents',
            'PasteFromOfficeEnhanced',
            'CaseChange'
        ],
        font_style: 'Arial, Helvetica, sans-serif'
    };

    const handleOnChangeCKEditor = (event, editor) => {
        const htmlContent = editor.getData();
        validateRichTextField(htmlContent);
        props?.setSettingIframe(prevState => {
            let newState = cloneDeep(prevState);
            newState[props?.name] = JSON.stringify(htmlContent);
            return newState;
        });
    };

    const validateRichTextField = htmlContent => {
        let contentText = getRichTextFieldContent(htmlContent);
        const contentHtmlEscape = Utils.escapeHtmlCharacters(contentText);
        //checking field required
        if (props?.required && !contentHtmlEscape) {
            return props?.setErrMsg(preState => {
                let newState = preState;
                newState[props?.name] = I18n.get('COMM01MSG11');
                return newState;
            });
        }

        //checking field over length
        switch (props?.name) {
            case Constant.IFRAME_SETTING.COMPLETED_SEND_TICKET_MESSAGE:
                if (Utils.isStringOverLength(contentHtmlEscape, 2000)) {
                    return props?.setErrMsg(preState => {
                        let newState = preState;
                        newState[props?.name] = I18n.get('OVER-2000-CHARS');;
                        return newState;
                    });
                }
                break;
            case Constant.IFRAME_SETTING.COMPLETED_SEND_TICKET_CONTENT_MAIL:
                if (Utils.isStringOverLength(contentHtmlEscape, 2000)) {
                    return props?.setErrMsg(preState => {
                        let newState = preState;
                        newState[props?.name] = I18n.get('OVER-2000-CHARS');;
                        return newState;
                    });
                }
                break;
            default:
        }
        const validateSizeRichtextContent = JSON.stringify(htmlContent)
        const msgFieldOverSize = Utils.validateSizeOfFieldTicket(validateSizeRichtextContent);
        return props?.setErrMsg(preState => {
            let newState = preState;
            newState[props?.name] = msgFieldOverSize;
            return newState;
        });
    };

    return (
        <>
            <div className="form-addTicket-input">
                <div
                    id={`ckbox-${ckEditorId}`}
                    data-cy="richtext-editor"
                    className={`
                    editor 
                    sdesk-editor                 
                    ${props?.errMsg[props?.name] ? ' editor-err ' : ''}
                    `}
                >
                    {typeof window?.CKEDITOR?.ClassicEditor != 'undefined' && (
                        <CKEditor
                            editor={window.CKEDITOR.ClassicEditor}
                            data={Utils.isValidJSONString(props?.settingIframe[props?.name]) ? JSON.parse(props?.settingIframe[props?.name]) : ""}
                            config={editorConfiguration}
                            // onChange={handleOnChangeCKEditor}
                            onReady={editor => {
                                setSelftCKEditor5(editor);
                                editor.ui.view.toolbar.element.style.display = 'none';
                                // Init fontSize default
                                const fontSize = editor.commands.get('fontSize');
                                fontSize.execute({ value: '14px' });

                                // Init fontFamily default
                                const fontFamily = editor.commands.get('fontFamily');
                                fontFamily.execute({ value: 'Arial, Helvetica, sans-serif' });
                                editor.execute('fontSize', { value: '14px' });
                                editor.execute('fontFamily', {
                                    value: 'Arial, Helvetica, sans-serif'
                                });

                                editor.model.document.on('change', (event, editorData) => {
                                    handleOnChangeCKEditor(event, editor);
                                });
                            }}
                            onFocus={(event, editor) => {
                                window.editor = editor;
                                document
                                    .querySelectorAll(
                                        `.editor .ck.ck-sticky-panel__content > .ck.ck-toolbar[aria-label="Editor toolbar"]`
                                    )
                                    .forEach(el => {
                                        el.style.display = 'none';
                                    });
                                editor.ui.view.toolbar.element.style.display = 'block';
                            }}
                        />
                    )}
                </div>
                {props?.errMsg[props?.name] && (
                    <p className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error">
                        {props?.errMsg[props?.name]}
                    </p>
                )}
            </div>
        </>
    );
};
